import {
    asResolvedKernel,
    DEFINITION_SOURCES,
    KERNEL_RESOLUTION_TIERS,
    normalizeKernelCommand,
} from "./kernelResolver.js";
import { currentBrowserStorageOwner } from "./browserStorageScope.js";

export const KERNEL_AUTHOR_HISTORY_PREFIX =
    "originChat.kernelAuthorHistory.v2";
const LEGACY_KERNEL_AUTHOR_HISTORY_PREFIX =
    "originChat.kernelAuthorHistory.v1";

function candidateParts(value) {
    const candidate = value?.resolvedDefinition ? value : null;
    const resolvedDefinition = asResolvedKernel(
        candidate?.resolvedDefinition ?? value,
    );
    const body = resolvedDefinition.body || {};
    const metadata = candidate?.metadata || {};
    return { candidate, resolvedDefinition, body, metadata };
}

function field(metadata, body, camel, snake = camel) {
    return metadata?.[camel]
        ?? (snake !== camel ? metadata?.[snake] : undefined)
        ?? body?.[camel]
        ?? (snake !== camel ? body?.[snake] : undefined)
        ?? null;
}

function authorIdentity(id, label, extra = {}) {
    return Object.freeze({
        id,
        label,
        isViewer: false,
        isOrigin: false,
        ...extra,
    });
}

export function kernelAuthor(value, { viewerUserId = null } = {}) {
    const { candidate, resolvedDefinition, body, metadata } =
        candidateParts(value);
    const createdBy = field(metadata, body, "createdBy", "created_by");
    const createdByLabel =
        field(metadata, body, "createdByLabel", "created_by_label")
        || field(metadata, body, "authorLabel", "author_label");
    const objectOrigin =
        field(metadata, body, "objectOrigin", "object_origin");

    if (resolvedDefinition.definitionSource === DEFINITION_SOURCES.BUNDLED) {
        return authorIdentity("origin", "Origin", { isOrigin: true });
    }

    const stored =
        resolvedDefinition.definitionSource === DEFINITION_SOURCES.STORED;
    if (stored && objectOrigin === "origin_deployment") {
        return authorIdentity("origin", "Origin", { isOrigin: true });
    }

    if (stored && createdBy) {
        const isViewer = Boolean(
            viewerUserId && String(createdBy) === String(viewerUserId),
        );
        return authorIdentity(
            `user:${createdBy}`,
            isViewer ? "You" : (createdByLabel || "Unknown author"),
            { isViewer },
        );
    }

    if (
        resolvedDefinition.definitionSource === DEFINITION_SOURCES.LIVE
        || candidate?.tier === KERNEL_RESOLUTION_TIERS.VIEWER_OWNED
        || candidate?.tier === KERNEL_RESOLUTION_TIERS.LEGACY_VIEWER
        || !field(metadata, body, "tenantViewId", "tenant_view_id")
    ) {
        return authorIdentity(
            viewerUserId ? `user:${viewerUserId}` : "viewer-local",
            "You",
            { isViewer: true },
        );
    }

    return authorIdentity("unknown", "Unknown author");
}

function commandOf(value) {
    const { candidate, body, metadata } = candidateParts(value);
    return normalizeKernelCommand(
        candidate?.command
        || metadata?.command
        || body?.command,
    );
}

function historyKey(tenantId, viewerUserId) {
    const owner = currentBrowserStorageOwner(tenantId);
    if (!owner || String(viewerUserId || "") !== owner.userId) return null;
    return `${KERNEL_AUTHOR_HISTORY_PREFIX}:${owner.key}`;
}

function readHistory(storage, key) {
    if (!storage || !key) return {};
    try {
        const parsed = JSON.parse(storage.getItem(key) || "{}");
        return parsed && typeof parsed === "object" && !Array.isArray(parsed)
            ? parsed
            : {};
    } catch {
        return {};
    }
}

function availableStorage(storage) {
    if (storage !== undefined) return storage;
    try {
        return globalThis.localStorage;
    } catch {
        return null;
    }
}

export function kernelAuthorChange(
    value,
    {
        tenantId = null,
        viewerUserId = null,
        storage,
    } = {},
) {
    const targetStorage = availableStorage(storage);
    const command = commandOf(value);
    const current = kernelAuthor(value, { viewerUserId });
    const key = historyKey(tenantId, viewerUserId);
    const previous = command ? readHistory(targetStorage, key)[command] : null;
    const changed = Boolean(
        previous?.authorId
        && current.id
        && previous.authorId !== current.id,
    );
    return Object.freeze({
        command,
        current,
        previous: previous
            ? Object.freeze({
                  id: previous.authorId,
                  label: previous.authorLabel || "Unknown author",
              })
            : null,
        changed,
    });
}

export function recordKernelAuthor(
    value,
    {
        tenantId = null,
        viewerUserId = null,
        storage,
    } = {},
) {
    const targetStorage = availableStorage(storage);
    const command = commandOf(value);
    const key = historyKey(tenantId, viewerUserId);
    if (!command || !key || !targetStorage) return false;
    const author = kernelAuthor(value, { viewerUserId });
    const history = readHistory(targetStorage, key);
    history[command] = {
        authorId: author.id,
        authorLabel: author.label,
    };
    try {
        targetStorage.setItem(key, JSON.stringify(history));
        return true;
    } catch {
        return false;
    }
}

export function clearKernelAuthorHistory(storage) {
    const targetStorage = availableStorage(storage);
    if (!targetStorage) return;
    try {
        const keys = [];
        for (let index = 0; index < targetStorage.length; index += 1) {
            const key = targetStorage.key(index);
            if (
                key === KERNEL_AUTHOR_HISTORY_PREFIX
                || key?.startsWith(`${KERNEL_AUTHOR_HISTORY_PREFIX}:`)
                || key === LEGACY_KERNEL_AUTHOR_HISTORY_PREFIX
                || key?.startsWith(`${LEGACY_KERNEL_AUTHOR_HISTORY_PREFIX}:`)
            ) {
                keys.push(key);
            }
        }
        for (const key of keys) targetStorage.removeItem(key);
    } catch {
        // Storage can be unavailable in hardened/private browser contexts.
    }
}
